import {
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "../cx";
import styles from "./Popover.module.css";

export type Placement =
  | "bottom-start"
  | "bottom-end"
  | "bottom"
  | "top-start"
  | "top-end"
  | "right-start"
  | "left-start";

export interface PopoverProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  /** Gap between anchor and panel, px. */
  offset?: number;
  /** Shift along the cross axis, px (e.g. the Reports flyout sits 12px right of the sidebar). */
  crossOffset?: number;
  matchAnchorWidth?: boolean;
  /** `menu` = legacy cl-dropdown-menu (2px radius, 0 5px 10px shadow); `card` = 0 0 20px popover shadow; `bare` = no chrome. */
  surface?: "menu" | "card" | "bare";
  /** Focus moves into the panel on open and back to the anchor on close. */
  autoFocus?: boolean;
  children: ReactNode;
}

interface Coords {
  top: number;
  left: number;
  minWidth?: number;
}

function compute(anchor: DOMRect, panel: DOMRect, placement: Placement, offset: number, cross: number): Coords {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top = 0;
  let left = 0;
  switch (placement) {
    case "bottom-start":
      top = anchor.bottom + offset;
      left = anchor.left + cross;
      break;
    case "bottom-end":
      top = anchor.bottom + offset;
      left = anchor.right - panel.width + cross;
      break;
    case "bottom":
      top = anchor.bottom + offset;
      left = anchor.left + anchor.width / 2 - panel.width / 2 + cross;
      break;
    case "top-start":
      top = anchor.top - panel.height - offset;
      left = anchor.left + cross;
      break;
    case "top-end":
      top = anchor.top - panel.height - offset;
      left = anchor.right - panel.width + cross;
      break;
    case "right-start":
      top = anchor.top + cross;
      left = anchor.right + offset;
      break;
    case "left-start":
      top = anchor.top + cross;
      left = anchor.left - panel.width - offset;
      break;
  }
  // Flip vertically when the panel would leave the viewport, then clamp so it is never off-screen.
  if (placement.startsWith("bottom") && top + panel.height > vh - 8 && anchor.top - panel.height - offset > 8) {
    top = anchor.top - panel.height - offset;
  }
  left = Math.max(8, Math.min(left, vw - panel.width - 8));
  top = Math.max(8, Math.min(top, Math.max(8, vh - panel.height - 8)));
  return { top, left };
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

export function Popover({
  open,
  onClose,
  anchorRef,
  placement = "bottom-start",
  offset = 4,
  crossOffset = 0,
  matchAnchorWidth = false,
  surface = "menu",
  autoFocus = true,
  className,
  style,
  children,
  ...rest
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const a = anchor.getBoundingClientRect();
    const next = compute(a, panel.getBoundingClientRect(), placement, offset, crossOffset);
    if (matchAnchorWidth) next.minWidth = a.width;
    setCoords((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.minWidth === next.minWidth ? prev : next,
    );
  }, [anchorRef, placement, offset, crossOffset, matchAnchorWidth]);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(reposition);
    if (panelRef.current) ro?.observe(panelRef.current);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      ro?.disconnect();
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchor?.contains(t)) return;
      onCloseRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Only the topmost layer reacts, so Escape in a menu inside a modal does not close the modal too.
      e.stopPropagation();
      onCloseRef.current();
      anchor?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    const panel = panelRef.current;
    anchor?.addEventListener("keydown", onKey);
    if (autoFocus) {
      queueMicrotask(() => {
        const first =
          panel?.querySelector<HTMLElement>("[data-autofocus]") ?? panel?.querySelector<HTMLElement>(FOCUSABLE);
        first?.focus();
      });
    }
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      anchor?.removeEventListener("keydown", onKey);
    };
  }, [open, anchorRef, autoFocus]);

  if (!open) return null;
  const pos: CSSProperties = coords
    ? { top: coords.top, left: coords.left, minWidth: coords.minWidth }
    : { top: 0, left: 0, visibility: "hidden" };

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: only catches Escape bubbling up from the focusable content
    <div
      ref={panelRef}
      className={cx(styles.popover, styles[surface], className)}
      style={{ ...pos, ...style }}
      {...rest}
      onKeyDown={(e) => {
        rest.onKeyDown?.(e);
        // React handler (not a native listener) so children run first and can claim Escape via preventDefault().
        if (e.key !== "Escape" || e.defaultPrevented) return;
        e.stopPropagation();
        onCloseRef.current();
        anchorRef.current?.focus();
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
